import { useState } from "react";

import { useToast } from "@/ds";
import {
  alterarSituacaoGrupo,
  alterarSituacaoSubgrupo,
  alterarSituacaoTipoObra,
} from "@/modules/financeiro-configuracoes";
import { useChaveIdempotencia } from "@/shared/hooks";
import { TOASTS } from "../../base-dados.constants";
import type { MensagemToast } from "../../base-dados.constants";
import type { AlvoSituacao } from "../../base-dados.linhas";
import {
  EFEITOS_TITULO_INATIVAR,
  ESCOPO_LABEL,
  GRUPO_TEXTOS,
  SITUACAO_INATIVO,
  SUBGRUPO_TEXTOS,
  TIPO_TEXTOS,
} from "./inativar-ativar-referencia.constants";
import type {
  EscopoInativacao,
  InativarAtivarReferenciaProps,
  InativarAtivarReferenciaViewModel,
} from "./types-inativar-ativar-referencia";

type Confirmar = (
  aviso: MensagemToast,
  enviar: (chave: string) => Promise<unknown>,
) => Promise<void>;
type Base = Pick<InativarAtivarReferenciaViewModel, "acao" | "onCancelar">;
type Situacao = typeof SITUACAO_INATIVO;

export function useInativarAtivarReferencia({
  acao: { alvo },
  onClose,
  onConcluido,
}: InativarAtivarReferenciaProps): InativarAtivarReferenciaViewModel {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const [escopoEscolhido, setEscopo] = useState<EscopoInativacao>("subgrupo");
  const situacao = SITUACAO_INATIVO;

  const confirmar: Confirmar = async (aviso, enviar) => {
    await comChave({ alvo, situacao }, enviar);
    toast.success(aviso.resumo, aviso.detalhe);
    onConcluido();
  };

  const base: Base = { acao: "inativar", onCancelar: onClose };

  if (alvo.tipo === "tipo-obra") return tipoObra(base, alvo, confirmar, situacao);
  if (alvo.tipo === "grupo") return grupo(base, alvo, confirmar, situacao);
  return subgrupo(base, alvo, confirmar, situacao, escopoEscolhido, setEscopo);
}

type Alvo<T extends AlvoSituacao["tipo"]> = Extract<AlvoSituacao, { tipo: T }>;

function tipoObra(
  base: Base,
  alvo: Alvo<"tipo-obra">,
  confirmar: Confirmar,
  situacao: Situacao,
): InativarAtivarReferenciaViewModel {
  const textos = TIPO_TEXTOS;
  const aviso = TOASTS.tipoInativado(alvo.codigo);
  return {
    ...base,
    titulo: textos.titulo,
    subtitulo: textos.subtitulo,
    confirmLabel: textos.confirmar,
    tituloEfeitos: EFEITOS_TITULO_INATIVAR,
    oQueAcontece: textos.efeitos(alvo.codigo),
    identificacao: { intro: textos.intro, titulo: `${alvo.codigo} · ${alvo.nome}` },
    escopo: null,
    onConfirmar: () =>
      confirmar(aviso, (idempotencyKey) =>
        alterarSituacaoTipoObra(alvo.tipoObraId, situacao, { idempotencyKey }),
      ),
  };
}

function grupo(
  base: Base,
  alvo: Alvo<"grupo">,
  confirmar: Confirmar,
  situacao: Situacao,
): InativarAtivarReferenciaViewModel {
  return {
    ...base,
    titulo: GRUPO_TEXTOS.titulo,
    subtitulo: GRUPO_TEXTOS.subtitulo,
    confirmLabel: GRUPO_TEXTOS.confirmar,
    tituloEfeitos: EFEITOS_TITULO_INATIVAR,
    oQueAcontece: GRUPO_TEXTOS.efeitos(alvo.tipos),
    identificacao: { intro: GRUPO_TEXTOS.intro(alvo.tipos), titulo: alvo.nome },
    escopo: null,
    onConfirmar: () =>
      confirmar(TOASTS.grupoInativado(alvo.nome), (idempotencyKey) =>
        alterarSituacaoGrupo(alvo.grupoId, situacao, { idempotencyKey }),
      ),
  };
}

function subgrupo(
  base: Base,
  alvo: Alvo<"subgrupo">,
  confirmar: Confirmar,
  situacao: Situacao,
  escopoEscolhido: EscopoInativacao,
  escolher: (escopo: EscopoInativacao) => void,
): InativarAtivarReferenciaViewModel {
  return {
    ...base,
    titulo: SUBGRUPO_TEXTOS.titulo,
    subtitulo: SUBGRUPO_TEXTOS.subtitulo,
    confirmLabel: SUBGRUPO_TEXTOS.confirmar,
    tituloEfeitos: EFEITOS_TITULO_INATIVAR,
    oQueAcontece: SUBGRUPO_TEXTOS.efeitos,
    identificacao: {
      intro: SUBGRUPO_TEXTOS.intro(alvo.tipoObra),
      titulo: SUBGRUPO_TEXTOS.registro(alvo.grupoNome, alvo.nome),
    },
    escopo: {
      rotulo: ESCOPO_LABEL,
      valor: escopoEscolhido,
      opcoes: [
        { valor: "subgrupo", rotulo: SUBGRUPO_TEXTOS.soSubgrupo(alvo.nome, alvo.grupoNome) },
        {
          valor: "grupo",
          rotulo: SUBGRUPO_TEXTOS.grupoInteiro(alvo.grupoNome, alvo.irmaos),
        },
      ],
      escolher,
    },
    onConfirmar: () =>
      escopoEscolhido === "grupo"
        ? confirmar(TOASTS.grupoInativado(alvo.grupoNome), (idempotencyKey) =>
            alterarSituacaoGrupo(alvo.grupoId, situacao, { idempotencyKey }),
          )
        : confirmar(TOASTS.subgrupoInativado(alvo.nome, alvo.grupoNome), (idempotencyKey) =>
            alterarSituacaoSubgrupo(alvo.grupoId, alvo.subgrupoId, situacao, { idempotencyKey }),
          ),
  };
}
